import React, { memo } from 'react';
import PropTypes from 'prop-types';

/**
 * StepIndicator Component
 * Renders individual step circles with appropriate styling based on state
 */
const StepIndicator = memo(({ stepNumber, label, description, isActive, isCompleted, isLast }) => {
  // Determine the CSS class for the step circle
  const getStepClass = () => {
    if (isCompleted) return 'step-circle completed';
    if (isActive) return 'step-circle active';
    return 'step-circle pending';
  };

  // Determine the CSS class for the connector line
  const getConnectorClass = () => {
    return isCompleted ? 'step-connector completed' : 'step-connector';
  };

  // Determine ARIA label for accessibility
  const getAriaLabel = () => {
    if (isCompleted) return `${label} - completed`;
    if (isActive) return `${label} - current step`;
    return `${label} - pending`;
  };

  return (
    <div className="step-indicator" aria-label={getAriaLabel()}>
      <div className={getStepClass()} aria-current={isActive ? 'step' : undefined}>
        {isCompleted ? (
          // Checkmark icon for completed steps
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <polyline points="20 6 9 17 4 12" />
          </svg>
        ) : (
          // Step number for active and pending steps
          <span>{stepNumber}</span>
        )}
      </div>
      
      {/* Connecting line (not rendered for last step) */}
      {!isLast && <div className={getConnectorClass()} aria-hidden="true" />}
      
      {/* Step label */}
      <div className="step-label">{label}</div>
    </div>
  );
});

StepIndicator.propTypes = {
  stepNumber: PropTypes.number.isRequired,
  label: PropTypes.string.isRequired,
  description: PropTypes.string,
  isActive: PropTypes.bool.isRequired,
  isCompleted: PropTypes.bool.isRequired,
  isLast: PropTypes.bool.isRequired
};

export default StepIndicator;
